/**
 * Navigation Store
 *
 * Manages navigation history for breadcrumbs, cross-project navigation,
 * and content slot coordination.
 */

// MUST be first import - enables MapSet for immer
import "@/lib/immer-config";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { generateId } from "@/utils";
import { apiClient } from "@/services/api/client";
import { SerializedNavigationStateSchema } from "@/schemas/navigation";
import {
  NavigationStore,
  NavigationState,
  NavigationEntry,
  NavigationError,
  ProjectNavigationTarget,
  NavigationResult,
  ContentSlot,
  AccessibleProjectInfo,
  DEFAULT_MAX_HISTORY_LENGTH,
  STORAGE_KEY,
  MAX_STORAGE_SIZE,
  PROJECTS_CACHE_TTL,
} from "./navigation-types";
import * as errorUtils from '@/utils/errors';

// ============================================================================
// Serialization Helpers (Maps don't JSON.stringify directly)
// ============================================================================

interface SerializedState {
  history: NavigationEntry[];
  currentIndex: number;
  maxHistoryLength: number;
  accessibleProjects: [string, AccessibleProjectInfo][];
  lastProjectsFetch: number;
  contentSlots: [string, ContentSlot][];
}

const serializeState = (state: NavigationState): SerializedState => ({
  history: state.history,
  currentIndex: state.currentIndex,
  maxHistoryLength: state.maxHistoryLength,
  accessibleProjects: Array.from(state.accessibleProjects.entries()),
  lastProjectsFetch: state.lastProjectsFetch,
  contentSlots: Array.from(state.contentSlots.entries()),
});

const deserializeState = (stored: SerializedState): Partial<NavigationState> => ({
  history: stored.history || [],
  currentIndex: stored.currentIndex ?? -1,
  maxHistoryLength: stored.maxHistoryLength || DEFAULT_MAX_HISTORY_LENGTH,
  accessibleProjects: new Map(stored.accessibleProjects || []),
  lastProjectsFetch: stored.lastProjectsFetch || 0,
  contentSlots: new Map(stored.contentSlots || []),
});

// ============================================================================
// Storage Helpers with LRU Eviction
// ============================================================================

const loadFromStorage = (): Partial<NavigationState> => {
  if (typeof window === "undefined") return {};

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const result = SerializedNavigationStateSchema.safeParse(JSON.parse(stored));
      if (!result.success) {
        console.warn("[NavigationStore] Stored state schema mismatch, resetting", result.error.flatten());
        return {};
      }
      return deserializeState(result.data);
    }
  } catch (error) {
    console.warn("[NavigationStore] Failed to load from storage:", error);
  }
  return {};
};

const saveToStorage = (state: NavigationState): void => {
  if (typeof window === "undefined") return;

  try {
    const serialized = JSON.stringify(serializeState(state));

    if (serialized.length > MAX_STORAGE_SIZE) {
      // LRU eviction: remove oldest entries until under limit
      const trimmedHistory = state.history.slice(-Math.floor(state.history.length / 2));
      const trimmedState: NavigationState = {
        ...state,
        history: trimmedHistory,
        currentIndex: Math.min(state.currentIndex, trimmedHistory.length - 1),
      };
      const trimmedSerialized = JSON.stringify(serializeState(trimmedState));
      sessionStorage.setItem(STORAGE_KEY, trimmedSerialized);
      console.warn("[NavigationStore] History trimmed due to size limit");
    } else {
      sessionStorage.setItem(STORAGE_KEY, serialized);
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      // Clear and log - storage is full
      sessionStorage.removeItem(STORAGE_KEY);
      console.error("[NavigationStore] Storage quota exceeded, history cleared");
    } else {
      console.warn("[NavigationStore] Failed to save to storage:", error);
    }
  }
};

// ============================================================================
// Initial State
// ============================================================================

const storedState = loadFromStorage();

const initialState: NavigationState = {
  // History tracking
  history: storedState.history || [],
  currentIndex: storedState.currentIndex ?? -1,
  maxHistoryLength: storedState.maxHistoryLength || DEFAULT_MAX_HISTORY_LENGTH,

  // Cross-project cache
  accessibleProjects: storedState.accessibleProjects || new Map(),
  lastProjectsFetch: storedState.lastProjectsFetch || 0,

  // Content slots
  contentSlots: storedState.contentSlots || new Map(),

  // Error state
  lastError: null,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useNavigationStore = create<NavigationStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,

      // ========================================================================
      // History Management
      // ========================================================================

      pushNavigation: (entry) => {
        set((state) => {
          const newEntry: NavigationEntry = {
            ...entry,
            id: generateId("nav"),
            timestamp: Date.now(),
          };

          // If we're not at the end of history, truncate forward entries
          if (state.currentIndex < state.history.length - 1) {
            state.history = state.history.slice(0, state.currentIndex + 1);
          }

          // Check for duplicate (same project/page as current entry)
          const currentEntry = state.history[state.currentIndex];
          if (
            currentEntry &&
            currentEntry.projectId === newEntry.projectId &&
            currentEntry.pageId === newEntry.pageId
          ) {
            // Don't add duplicate, just update timestamp and parameters
            state.history[state.currentIndex] = {
              ...currentEntry,
              timestamp: Date.now(),
              parameters: newEntry.parameters,
            };
            return;
          }

          // Add new entry
          state.history.push(newEntry);

          // Enforce max history length
          if (state.history.length > state.maxHistoryLength) {
            state.history = state.history.slice(-state.maxHistoryLength);
          }

          // Update current index to point to new entry
          state.currentIndex = state.history.length - 1;
        });

        // Persist after state update
        saveToStorage(get());
      },

      navigateBack: () => {
        const state = get();
        if (state.currentIndex <= 0) {
          return null;
        }

        const newIndex = state.currentIndex - 1;
        const entry = state.history[newIndex];

        set((s) => {
          s.currentIndex = newIndex;
        });

        saveToStorage(get());
        return entry || null;
      },

      navigateForward: () => {
        const state = get();
        if (state.currentIndex >= state.history.length - 1) {
          return null;
        }

        const newIndex = state.currentIndex + 1;
        const entry = state.history[newIndex];

        set((s) => {
          s.currentIndex = newIndex;
        });

        saveToStorage(get());
        return entry || null;
      },

      navigateToHistoryIndex: (index: number) => {
        const state = get();
        if (index < 0 || index >= state.history.length) {
          return null;
        }

        set((s) => {
          s.currentIndex = index;
        });

        saveToStorage(get());
        return state.history[index] || null;
      },

      clearHistory: () => {
        set((state) => {
          state.history = [];
          state.currentIndex = -1;
        });
        saveToStorage(get());
      },

      updateScrollPosition: (position: { x: number; y: number }) => {
        set((state) => {
          const currentEntry = state.history[state.currentIndex];
          if (currentEntry) {
            currentEntry.scrollPosition = position;
          }
        });
        saveToStorage(get());
      },

      getBreadcrumbs: (projectId?: string) => {
        const { history, currentIndex } = get();

        // Get entries up to current index
        const relevantHistory = history.slice(0, currentIndex + 1);

        // If projectId specified, filter to that project
        if (projectId) {
          const projectEntries = relevantHistory.filter(
            (entry) => entry.projectId === projectId
          );

          // Deduplicate by pageId, keeping the most recent version
          const seen = new Map<string, NavigationEntry>();
          for (const entry of projectEntries) {
            seen.set(entry.pageId, entry);
          }

          // Return in order of first appearance
          const uniqueEntries: NavigationEntry[] = [];
          const addedPageIds = new Set<string>();

          for (const entry of projectEntries) {
            if (!addedPageIds.has(entry.pageId)) {
              uniqueEntries.push(seen.get(entry.pageId)!);
              addedPageIds.add(entry.pageId);
            }
          }

          return uniqueEntries;
        }

        return relevantHistory;
      },

      // ========================================================================
      // Cross-Project Navigation
      // ========================================================================

      setAccessibleProjects: (projects: Map<string, AccessibleProjectInfo>) => {
        set((state) => {
          state.accessibleProjects = projects;
          state.lastProjectsFetch = Date.now();
        });
        saveToStorage(get());
      },

      checkProjectAccess: async (projectId: string): Promise<boolean> => {
        try {
          const response = await apiClient.get(`/projects/${projectId}/can-access`);

          if (response.status === 200 && response.data?.data?.canAccess) {
            // Cache the project info with pages
            set((state) => {
              state.accessibleProjects.set(projectId, {
                name: response.data.data.projectName || "Unknown Project",
                pages: response.data.data.pages,
              });
              state.lastProjectsFetch = Date.now();
            });
            return true;
          }
          return false;
        } catch (error: unknown) {
          // For any error, assume no access
          return false;
        }
      },

      fetchAccessibleProjects: async (): Promise<Map<string, AccessibleProjectInfo>> => {
        const state = get();

        // Use cache if fresh (5 min TTL)
        if (
          state.accessibleProjects.size > 0 &&
          state.lastProjectsFetch &&
          Date.now() - state.lastProjectsFetch < PROJECTS_CACHE_TTL
        ) {
          return state.accessibleProjects;
        }

        try {
          const response = await apiClient.get('/projects/accessible');

          if (response.status === 200 && response.data?.data) {
            const projectsMap = new Map<string, AccessibleProjectInfo>();

            for (const project of response.data.data) {
              projectsMap.set(project.id, {
                name: project.name,
                organizationId: project.organizationId,
                organizationName: project.organizationName,
                pages: project.pages,
              });
            }

            set((state) => {
              state.accessibleProjects = projectsMap;
              state.lastProjectsFetch = Date.now();
            });

            return projectsMap;
          }

          return new Map();
        } catch (error) {
          console.error('[NavigationStore] Failed to fetch accessible projects:', error);
          return state.accessibleProjects;
        }
      },

      navigateToProject: async (target: ProjectNavigationTarget): Promise<NavigationResult> => {
        const { projectId, pageId, openInNewTab } = target;

        // Check access first - call API directly to get the reason
        try {
          const response = await apiClient.get(`/projects/${projectId}/can-access`);

          if (response.status === 200 && response.data?.data) {
            const { canAccess, reason, projectName, pages } = response.data.data;

            if (canAccess) {
              // Cache the project info
              set((state) => {
                state.accessibleProjects.set(projectId, {
                  name: projectName || "Unknown Project",
                  pages,
                });
                state.lastProjectsFetch = Date.now();
              });
            } else {
              // Access denied - use the reason from the backend
              const error: NavigationError = reason === "not_found"
                ? { type: "not_found", projectId }
                : { type: "access_denied", projectId, reason: "You do not have permission to access this project" };

              set((state) => {
                state.lastError = error;
              });

              return { success: false, error };
            }
          } else {
            // Unexpected response
            const error: NavigationError = {
              type: "access_denied",
              projectId,
              reason: "Unable to verify project access",
            };

            set((state) => {
              state.lastError = error;
            });

            return { success: false, error };
          }
        } catch (error: unknown) {
          // Network or server error
          const navError: NavigationError = {
            type: "network_error",
            message: errorUtils.getErrorMessageOrFallback(error, "Failed to check project access"),
          };

          set((state) => {
            state.lastError = navError;
          });

          return { success: false, error: navError };
        }

        // Build navigation URL
        let url = `/viewer/${projectId}`;
        if (pageId) {
          url += `/page/${pageId}`;
        }

        // Navigate
        if (openInNewTab) {
          const newWindow = window.open(url, "_blank");
          // Check if popup was blocked
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            const error: NavigationError = {
              type: "network_error",
              message: "Popup was blocked. Please allow popups for this site.",
            };
            set((state) => {
              state.lastError = error;
            });
            return { success: false, error };
          }
        } else {
          // Full page navigation for cross-project
          window.location.href = url;
        }

        return { success: true };
      },

      // ========================================================================
      // Content Slots
      // ========================================================================

      registerContentSlot: (containerId: string, contentIds: string[]) => {
        if (contentIds.length === 0) {
          console.warn("[NavigationStore] Cannot register content slot with empty contentIds");
          return;
        }

        set((state) => {
          // Preserve activeContentId if slot already exists and the ID is still valid
          const existingSlot = state.contentSlots.get(containerId);
          const activeContentId =
            existingSlot?.activeContentId && contentIds.includes(existingSlot.activeContentId)
              ? existingSlot.activeContentId
              : contentIds[0];

          state.contentSlots.set(containerId, {
            containerId,
            activeContentId,
            availableContentIds: contentIds,
          });
        });

        // Persist after state update
        saveToStorage(get());
      },

      setActiveContent: (containerId: string, contentId: string) => {
        set((state) => {
          const slot = state.contentSlots.get(containerId);
          if (slot && slot.availableContentIds.includes(contentId)) {
            slot.activeContentId = contentId;
          } else {
            console.warn(
              `[NavigationStore] Invalid content swap: container=${containerId}, content=${contentId}`
            );
          }
        });

        // Persist after state update
        saveToStorage(get());
      },

      getActiveContent: (containerId: string) => {
        const slot = get().contentSlots.get(containerId);
        return slot?.activeContentId ?? null;
      },

      unregisterContentSlot: (containerId: string) => {
        set((state) => {
          state.contentSlots.delete(containerId);
        });

        // Persist after state update
        saveToStorage(get());
      },

      // ========================================================================
      // Error Handling
      // ========================================================================

      setError: (error: NavigationError | null) => {
        set((state) => {
          state.lastError = error;
        });
      },

      clearError: () => {
        set((state) => {
          state.lastError = null;
        });
      },

      // ========================================================================
      // Persistence
      // ========================================================================

      persistState: () => {
        saveToStorage(get());
      },

      restoreState: () => {
        if (typeof window === "undefined") return;

        try {
          const stored = sessionStorage.getItem(STORAGE_KEY);
          if (!stored) return;

          const parseResult = SerializedNavigationStateSchema.safeParse(JSON.parse(stored));
          if (!parseResult.success) {
            console.warn("[NavigationStore] Stored state schema mismatch on restore", parseResult.error.flatten());
            return;
          }
          const restored = deserializeState(parseResult.data);

          set((state) => {
            if (restored.history) state.history = restored.history;
            if (typeof restored.currentIndex === "number") {
              state.currentIndex = Math.min(
                restored.currentIndex,
                (restored.history?.length || 1) - 1
              );
            }
            if (restored.accessibleProjects) {
              state.accessibleProjects = restored.accessibleProjects;
            }
            if (typeof restored.lastProjectsFetch === "number") {
              state.lastProjectsFetch = restored.lastProjectsFetch;
            }
            if (restored.contentSlots) {
              state.contentSlots = restored.contentSlots;
            }
          });
        } catch (error) {
          console.warn("[NavigationStore] Failed to restore state:", error);
        }
      },

      // ========================================================================
      // Reset
      // ========================================================================

      reset: () => {
        set((state) => {
          state.history = [];
          state.currentIndex = -1;
          state.accessibleProjects.clear();
          state.lastProjectsFetch = 0;
          state.contentSlots.clear();
          state.lastError = null;
        });

        // Clear persisted state
        if (typeof window !== "undefined") {
          try {
            sessionStorage.removeItem(STORAGE_KEY);
          } catch {
            // Ignore storage errors
          }
        }
      },
    }))
  )
);

// ============================================================================
// Selector Hooks
// ============================================================================

/** Get the full navigation history */
export const useNavigationHistory = () =>
  useNavigationStore((state) => state.history);

/** Get the current navigation entry */
export const useCurrentNavigation = () =>
  useNavigationStore((state) =>
    state.currentIndex >= 0 ? state.history[state.currentIndex] : null
  );

/** Get breadcrumb entries (history up to current index) */
export const useBreadcrumbEntries = () =>
  useNavigationStore((state) =>
    state.history.slice(0, state.currentIndex + 1)
  );

/** Check if can navigate back */
export const useCanNavigateBack = () =>
  useNavigationStore((state) => state.currentIndex > 0);

/** Check if can navigate forward */
export const useCanNavigateForward = () =>
  useNavigationStore((state) => state.currentIndex < state.history.length - 1);

/** Get navigation position info */
export const useNavigationPosition = () =>
  useNavigationStore(
    useShallow((state) => ({
      currentIndex: state.currentIndex,
      historyLength: state.history.length,
      canGoBack: state.currentIndex > 0,
      canGoForward: state.currentIndex < state.history.length - 1,
    }))
  );

/** Get content slot for a container */
export const useContentSlot = (containerId: string) =>
  useNavigationStore((state) => state.contentSlots.get(containerId));

/** Get the last navigation error */
export const useNavigationError = () =>
  useNavigationStore((state) => state.lastError);

/** Get accessible projects map */
export const useAccessibleProjects = () =>
  useNavigationStore((state) => state.accessibleProjects);

/** Get cross-project navigation state */
export const useNavigationState = () =>
  useNavigationStore(
    useShallow((state) => ({
      lastError: state.lastError,
      accessibleProjects: state.accessibleProjects,
      lastProjectsFetch: state.lastProjectsFetch,
    }))
  );

/** Get navigation actions */
export const useNavigationActions = () =>
  useNavigationStore(
    useShallow((state) => ({
      pushNavigation: state.pushNavigation,
      navigateBack: state.navigateBack,
      navigateForward: state.navigateForward,
      navigateToHistoryIndex: state.navigateToHistoryIndex,
      getBreadcrumbs: state.getBreadcrumbs,
      clearHistory: state.clearHistory,
      updateScrollPosition: state.updateScrollPosition,
      navigateToProject: state.navigateToProject,
      checkProjectAccess: state.checkProjectAccess,
      fetchAccessibleProjects: state.fetchAccessibleProjects,
      setAccessibleProjects: state.setAccessibleProjects,
      setError: state.setError,
      clearError: state.clearError,
    }))
  );

/** Get content slot actions */
export const useContentSlotActions = () =>
  useNavigationStore(
    useShallow((state) => ({
      registerContentSlot: state.registerContentSlot,
      setActiveContent: state.setActiveContent,
      getActiveContent: state.getActiveContent,
      unregisterContentSlot: state.unregisterContentSlot,
    }))
  );
